import { TFilterWithViewAllRef } from '@/components/common/filter-with-view-all/type';
import { SortDirection } from '@/constant';
import { ICommonApplicant } from '@/services';
import { CommonSelectors, useAppSelector } from '@/stores';
import { useCallback, useMemo, useRef } from 'react';

import {
	FilterValue,
	SorterResult,
	SortOrder,
	TablePaginationConfig,
} from 'antd/es/table/interface';

import { includes, keys } from 'ramda';

import { getColumns } from '../../define-assignment.config';
import { ApplicantListProps } from '.';

export const useLogic = (props: ApplicantListProps) => {
	const fetching = useAppSelector(
		CommonSelectors.selectFetchingCommonApplicantList
	);
	const applicantList = useAppSelector(
		CommonSelectors.selectCommonApplicantList
	);
	const total = useAppSelector(CommonSelectors.selectTotalCommonApplicantList);
	const reduxParams = useAppSelector(
		CommonSelectors.selectParamsCommonApplicantList
	);

	const filterRef = useRef<TFilterWithViewAllRef>(null);

	const dataSource = useMemo(() => {
		return applicantList.map((item) => ({
			...item,
			disabled:
				props.applicantRowSelection?.defaultSelectedRowKeys?.length &&
				props.applicantRowSelection?.defaultSelectedRowKeys?.includes(item.id)
					? false
					: item.disabled,
		}));
	}, [applicantList, props.applicantRowSelection]);

	const onPageChange = useCallback(
		(page: number) => {
			props.fetchData({
				page,
			});
		},
		[props.fetchData]
	);

	const onSort = useCallback(
		(field: string, order?: SortOrder) => {
			const sortOrderMap = {
				GENDER: { ascend: SortDirection.DESC, descend: SortDirection.ASC },
				REGIST_DT: { ascend: SortDirection.DESC, descend: SortDirection.ASC },
				DEFAULT: { ascend: SortDirection.ASC, descend: SortDirection.DESC },
			};

			const sortOrder = order
				? sortOrderMap[
						includes(field, keys(sortOrderMap))
							? (field as keyof typeof sortOrderMap)
							: 'DEFAULT'
					][order]
				: null;

			props.fetchData({
				sortSql: sortOrder ? `APPLICANT.${field}.${sortOrder}` : '',
			});
		},
		[props.fetchData]
	);

	const handleTableChange = useCallback(
		(
			pagination: TablePaginationConfig,
			filters: Record<string, FilterValue | null>,
			sorter: SorterResult<ICommonApplicant> | SorterResult<ICommonApplicant>[]
		) => {
			const mapFieldName = (field: string): string => {
				switch (field) {
					case 'genderName':
						return 'GENDER';
					case 'registDt':
						return 'REGIST_DT';
					case 'age':
						return 'BIRTH_DATE';
					default:
						return field;
				}
			};

			if (!Array.isArray(sorter)) {
				onSort(mapFieldName(sorter.field as string), sorter.order);
			} else if (sorter.length > 0) {
				const primarySorter = sorter[0];
				onSort(
					mapFieldName(primarySorter.field as string),
					primarySorter.order
				);
			}
		},
		[onSort, props.fetchData]
	);

	const columns = useMemo(() => getColumns({ onSort }), [onSort]);
	const columnsApplicantSelected = useMemo(
		() => getColumns({ isApplicantSelected: true }),
		[]
	);

	return {
		filterRef,
		fetching,
		dataSource,
		total,
		reduxParams,
		columns,
		columnsApplicantSelected,
		onPageChange,
		handleTableChange,
	};
};
